

import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DropdownMenusModule } from 'src/app/_metronic/partials';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AcademicTerm,
} from 'src/shared/_tables/academic-term-table/academic-term-table.component';
import { SharedModule } from 'src/shared/shared.module';
import {
  AcademicTermSaveAction,
  AcademicTermSaveFailedEvent,
  AcademicTermSyncFailedEvent,
  FormAddAcademicTermComponent,
} from '../../import/components/form-add-academic-term/form-add-academic-term.component';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { AcademicTermListResponse, AcademicTermModel } from 'src/app/services/models/study-period/study-period.model';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { getSafeErrorText } from 'src/utils/http-error-message';

interface AcademicTermDataSource {
  data: AcademicTerm[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

interface CurrentAcademicTermContext {
  term: string;
  year: number;
}

@Component({
  selector: 'app-study-period',
  imports: [
    SharedModule,
    TranslateModule,
    DropdownMenusModule,
    FormAddAcademicTermComponent,
    ReactiveFormsModule
  ],
  templateUrl: './study-period.component.html',
  styleUrl: './study-period.component.scss',
})
export class StudyPeriodComponent implements OnInit {

  @ViewChild('tableCellAction', { static: true })
  tableCellAction!: TemplateRef<any>;
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild(FormAddAcademicTermComponent)
  academicTermForm!: FormAddAcademicTermComponent;
  @ViewChild('deleteAcademicTermConfirmDialog')
  deleteAcademicTermConfirmDialog!: ConfirmDialogComponent;
  @ViewChild('deleteAcademicTermSuccessDialog')
  deleteAcademicTermSuccessDialog!: ConfirmDialogComponent;
  @ViewChild('deleteAcademicTermErrorDialog')
  deleteAcademicTermErrorDialog!: ConfirmDialogComponent;
  @ViewChild('createAcademicTermSuccessDialog')
  createAcademicTermSuccessDialog!: ConfirmDialogComponent;
  @ViewChild('createAcademicTermErrorDialog')
  createAcademicTermErrorDialog!: ConfirmDialogComponent;
  @ViewChild('editAcademicTermSuccessDialog')
  editAcademicTermSuccessDialog!: ConfirmDialogComponent;
  @ViewChild('editAcademicTermErrorDialog')
  editAcademicTermErrorDialog!: ConfirmDialogComponent;
  @ViewChild('syncAcademicTermSuccessDialog')
  syncAcademicTermSuccessDialog!: ConfirmDialogComponent;
  @ViewChild('syncAcademicTermErrorDialog')
  syncAcademicTermErrorDialog!: ConfirmDialogComponent;

  studyPeriodService = inject(StudyPeriodService);
  translate = inject(TranslateService);
  authService = inject(AuthService);
  academicTerms = signal<AcademicTermDataSource>({
    data: [],
    limit: 10,
    offset: 0,
    page: 1,
    total: 0,
    total_page: 1,
  });
  tableLoading = signal(false);

  columns: any[] = [];
  widgetColumns: any[] = [];
  form: FormGroup
  fb = inject(FormBuilder);
  cdr = inject(ChangeDetectorRef);
  pageSizeOptions = [10, 20, 50];
  isDeleting = false;
  yearOptions: string[] = [];
  deleteAcademicTermErrorMessage = 'ไม่สามารถลบรายการได้ กรุณาลองใหม่อีกครั้ง';
  createAcademicTermErrorMessage = 'ไม่สามารถเพิ่มรายการได้ กรุณาลองใหม่อีกครั้ง';
  editAcademicTermErrorMessage = 'ไม่สามารถแก้ไขรายการได้ กรุณาลองใหม่อีกครั้ง';
  syncAcademicTermErrorMessage = 'ไม่สามารถ Sync ข้อมูลรายวิชาได้ กรุณาลองใหม่อีกครั้ง';

  private destroyRef = inject(DestroyRef);
  private currentPage = 1;
  private currentLimit = 10;
  private currentSortParams: DatatableSortParams = {
    sort: null,
    direction: null,
  };
  private deletingAcademicTermId?: number;
  private readonly sortFieldMap: DatatableSortFieldMap = {
    index: 'sequence',
    term: 'name',
    year: 'year',
    studyPeriod: 'study_start_date',
    midtermPeriod: 'midterm_start_date',
    finalPeriod: 'final_start_date',
  };



  ngOnInit(): void {
    this.yearOptions = this.buildYearOptions();
    this.initFormSerch();
    this.initColumn();
    this.loadAcademicTerms();
    this.onSearch();
  }

  initFormSerch(): void {
    this.form = this.fb.group({
      name: [null],
      year: [null]
    })
  }

  onSearch(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.loadAcademicTerms(1));
  }

  loadAcademicTerms(page = this.currentPage): void {
    this.tableLoading.set(true);
    this.currentPage = page;
    const { name, year } = this.form.getRawValue();

    this.studyPeriodService.getAcademicTermsList({
      name,
      year: this.toChristianYear(year),
      page: this.currentPage,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).subscribe({
      next: (res) => {
        this.academicTerms.set(this.createAcademicTermDataSource(res));
        this.tableLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.tableLoading.set(false);
      },
    });
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.academicTerms().total_page || page === this.currentPage) {
      return;
    }

    this.loadAcademicTerms(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadAcademicTerms(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadAcademicTerms(1);
  }

  initColumn(): void {
    this.columns = [
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 64,
        minWidth: 64,
        maxWidth: 64,
      },
      {
        prop: 'term',
        name: 'เทอมการศึกษา',
        sortable: true,
        width: 140,
        minWidth: 140,
        maxWidth: 140,
      },
      {
        prop: 'year',
        name: 'ปีการศึกษา',
        sortable: false,
        width: 120,
        minWidth: 120,
        maxWidth: 120,

      },
      {
        prop: 'studyPeriod',
        name: 'ระยะเวลาการศึกษา',
        sortable: true,
      },
      {
        prop: 'midtermPeriod',
        name: 'ช่วงสอบกลางภาค',
        sortable: true,
      },
      {
        prop: 'finalPeriod',
        name: 'ช่วงสอบปลายภาค',
        sortable: true,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.tableCellAction,
        width: 104,
        minWidth: 104,
        maxWidth: 104,
        cellClass: 'action-cell',
        
        headerClass: 'action-header',
      }
    ];

    this.widgetColumns = this.columns.filter((i) => i.prop !== 'action');
  }

  onAcademicTermSaved(action: AcademicTermSaveAction): void {
    this.loadAcademicTerms(action === 'create' ? 1 : this.currentPage);

    if (action === 'create') {
      this.createAcademicTermSuccessDialog.open();
      return;
    }

    this.editAcademicTermSuccessDialog.open();
  }

  onAcademicTermSaveFailed({ action, message }: AcademicTermSaveFailedEvent): void {
    if (action === 'create') {
      this.openCreateAcademicTermErrorDialog(message);
      return;
    }

    this.openEditAcademicTermErrorDialog(message);
  }

  onAcademicTermSynced(): void {
    this.loadAcademicTerms(this.currentPage);
    this.syncAcademicTermSuccessDialog.open();
  }

  onAcademicTermSyncFailed({ message }: AcademicTermSyncFailedEvent): void {
    this.syncAcademicTermErrorMessage = message || 'ไม่สามารถ Sync ข้อมูลรายวิชาได้ กรุณาลองใหม่อีกครั้ง';
    this.syncAcademicTermErrorDialog.open();
  }

  onAddAcademicTerm(): void {
    this.academicTermForm.openModal();
  }

  onEditAcademicTerm(row: AcademicTerm): void {
    if (row.canEdit !== true) {
      return;
    }

    this.academicTermForm.openModal(row.id);
  }

  onDeleteAcademicTerm(row: AcademicTerm): void {
    if (row.canDelete !== true) {
      return;
    }

    this.deletingAcademicTermId = row.id;
    this.deleteAcademicTermConfirmDialog.open();
  }

  onConfirmDeleteAcademicTerm(): void {
    if (this.deletingAcademicTermId == null || this.isDeleting) {
      return;
    }

    this.isDeleting = true;

    this.studyPeriodService.deleteStudyPeriod(this.deletingAcademicTermId).subscribe({
      next: () => {
        this.deletingAcademicTermId = undefined;
        this.loadAcademicTerms();
        this.closeDeleteConfirmThen(() => this.deleteAcademicTermSuccessDialog.open());
      },
      error: (err) => {
        console.error(err);
        this.deletingAcademicTermId = undefined;
        const message = this.getApiErrorMessage(
          err,
          'ไม่สามารถลบรายการได้ กรุณาลองใหม่อีกครั้ง'
        );

        this.closeDeleteConfirmThen(() => this.openDeleteAcademicTermErrorDialog(message));
        this.isDeleting = false;
      },
      complete: () => {
        this.isDeleting = false;
      },
    });
  }

  onCancelDeleteAcademicTerm(): void {
    this.deletingAcademicTermId = undefined;
  }

  private createAcademicTermDataSource(response: AcademicTermListResponse | AcademicTermModel[]): AcademicTermDataSource {
    const items = Array.isArray(response) ? response : response.data;
    const page = Array.isArray(response) ? 1 : response.page;
    const limit = Array.isArray(response) ? this.currentLimit : response.limit;
    const total = Array.isArray(response) ? items.length : response.total;
    const totalPage = Array.isArray(response) ? Math.max(1, Math.ceil(total / limit)) : response.total_pages;
    const currentAcademicTerm = this.getCurrentAcademicTermContext();
    const data = items.map((item) => {
      const year = this.toBuddhistYear(item.year);
      const term = item.name;
      const permissions = this.getAcademicTermPermissions(term, year, currentAcademicTerm);

      return {
        id: item.id,
        sequence: item.sequence,
        term,
        year,
        studyPeriod: this.formatDateRange(item.study_start_date, item.study_end_date),
        midtermPeriod: this.formatDateRange(item.midterm_start_date, item.midterm_end_date),
        finalPeriod: this.formatDateRange(item.final_start_date, item.final_end_date),
        syncAt: item.sync_at ?? null,
        canEdit: permissions.canEdit,
        canDelete: permissions.canDelete,
      };
    });

    return {
      data,
      limit,
      offset: (page - 1) * limit,
      page,
      total,
      total_page: totalPage,
    };
  }

  private formatDateRange(startDate: string, endDate: string): string {
    return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
  }

  private formatDate(value: string): string {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = this.toBuddhistYear(date.getFullYear());

    return `${day}/${month}/${year}`;
  }

  private toChristianYear(value: unknown): string | null {
    if (value == null || value === '' || value === 0 || value === '0') {
      return null;
    }

    const year = Number(value);

    if (!Number.isFinite(year) || year <= 0) {
      return null;
    }

    return String(year > 2400 ? year - 543 : year);
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private getCurrentBuddhistYear(): number {
    return this.toBuddhistYear(new Date().getFullYear());
  }

  private buildYearOptions(): string[] {
    const sessionYear = this.authService.currentUser()?.academic_term?.year;
    const baseYear = sessionYear == null
      ? this.getCurrentBuddhistYear()
      : this.toBuddhistYear(Number(sessionYear));

    return [baseYear - 1, baseYear, baseYear + 1].map((year) => String(year));
  }

  private getCurrentAcademicTermContext(): CurrentAcademicTermContext | null {
    const academicTerm = this.authService.currentUser()?.academic_term;
    const year = Number(academicTerm?.year);

    if (academicTerm == null || !Number.isFinite(year) || year <= 0) {
      return null;
    }

    return {
      term: String(academicTerm.name),
      year: this.toBuddhistYear(year),
    };
  }

  private getAcademicTermPermissions(
    term: string,
    year: number,
    currentAcademicTerm: CurrentAcademicTermContext | null
  ): Pick<AcademicTerm, 'canEdit' | 'canDelete'> {
    if (currentAcademicTerm == null) {
      return {
        canEdit: true,
        canDelete: true,
      };
    }

    const isPastYear = year < currentAcademicTerm.year;
    const isCurrentTerm = year === currentAcademicTerm.year
      && String(term) === currentAcademicTerm.term;

    return {
      canEdit: !isPastYear,
      canDelete: !isPastYear && !isCurrentTerm,
    };
  }

  private openDeleteAcademicTermErrorDialog(message: string): void {
    this.deleteAcademicTermErrorMessage = message;
    this.cdr.detectChanges();
    this.deleteAcademicTermErrorDialog.open();
  }

  private closeDeleteConfirmThen(openNextDialog: () => void): void {
    const modalEl = document.getElementById('deleteAcademicTermConfirm');

    if (modalEl == null) {
      this.deleteAcademicTermConfirmDialog.close();
      openNextDialog();
      return;
    }

    modalEl.addEventListener('hidden.bs.modal', openNextDialog, { once: true });
    this.deleteAcademicTermConfirmDialog.close();
  }

  private openCreateAcademicTermErrorDialog(message: string): void {
    this.createAcademicTermErrorMessage = message;
    this.cdr.detectChanges();
    this.createAcademicTermErrorDialog.open();
  }

  private openEditAcademicTermErrorDialog(message: string): void {
    this.editAcademicTermErrorMessage = message;
    this.cdr.detectChanges();
    this.editAcademicTermErrorDialog.open();
  }

  private getApiErrorMessage(error: unknown, fallbackMessage: string): string {
    const responseError = (error as { error?: unknown } | null | undefined)?.error;
    const parsedResponseError = this.parseErrorResponse(responseError);

    if (parsedResponseError != null && typeof parsedResponseError === 'object') {
      const errorRecord = parsedResponseError as { errors?: unknown; error?: unknown; message?: unknown };

      if (Array.isArray(errorRecord.errors) && errorRecord.errors.length > 0) {
        const errors = errorRecord.errors
          .map((item) => this.getErrorText(item))
          .filter((item) => item !== '');

        if (errors.length > 0) {
          return errors.join('\n');
        }
      }

      const errorText = this.getErrorText(errorRecord.error);
      const messageText = this.getErrorText(errorRecord.message);
      const message = errorText !== '' ? errorText : messageText;

      return message !== '' ? message : fallbackMessage;
    }

    const responseErrorText = this.getErrorText(parsedResponseError);
    const messageText = this.getErrorText((error as { message?: unknown } | null | undefined)?.message);
    const message = responseErrorText !== '' ? responseErrorText : messageText;

    return message !== '' ? message : fallbackMessage;
  }

  private parseErrorResponse(value: unknown): unknown {
    if (typeof value !== 'string') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  private getErrorText(value: unknown): string {
    return getSafeErrorText(value);
  }
}
